import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Video, /*Gem,*/ LogOut, User, Users, History, Home, Mail, Sparkles, GraduationCap, Settings } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import CreditHistory from './CreditHistory';
import SupportModal from './SupportModal';
import FeedbackModal from './FeedbackModal';
import { getMyRole } from '../services/studioApi';
import './RightSideMenu.css';

const RightSideMenu = ({ isOpen, onClose, user, currentPlan, onOpenSubscription }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];
  const [showCreditHistory, setShowCreditHistory] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [studioRole, setStudioRole] = useState(null);

  

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Fetch studio role from API (user?.role from localStorage is unreliable)
  useEffect(() => {
    getMyRole()
      .then(({ role }) => setStudioRole(role || null))
      .catch(() => {});
  }, []);

  const getPlanBadge = (plan) => {
    const badges = {
      free: { text: t.pricing?.freePlan || 'Free', color: '#4caf50' },
      hobby: { text: t.pricing?.hobbyPlan || 'Hobby', color: '#2196f3' },
      pro: { text: t.pricing?.proPlan || 'Pro', color: '#f44336' }
    };
    return badges[plan] || badges.free;
  };

  const planBadge = getPlanBadge(currentPlan);

  const handleLogout = () => {
    onClose(); // Close menu first
    setShowFeedbackModal(true); // Show feedback modal
  };

  const handleLogoutConfirm = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate('/');
  };

  const handleDashboard = () => {
    navigate('/dashboard');
    onClose();
  };

  const handleMyVideos = () => {
    navigate('/videos');
    onClose();
  };

  const handleGallery = () => {
    navigate('/pgallery');
    onClose();
  };

  

  const handlePixelAI = () => {
    navigate('/pixel-ai');
    onClose();
  };

  const handleStudio = () => {
    navigate('/studio');
    onClose();
  };

  /*const handleChangePlan = () => {
    onClose();
    onOpenSubscription();
  };*/

  const handleCreditHistory = () => {
    setShowCreditHistory(true);
  };

  const handleCloseCreditHistory = () => {
    setShowCreditHistory(false);
  };
  
  const handleSettings = () => {
    navigate('/settings');
    onClose();
  };

  const handleContactSupport = () => {
    setShowSupportModal(true);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className={`right-menu-overlay ${isOpen ? 'active' : ''}`}
          onClick={onClose}
        />
      )}

      {/* Slide-in Menu */}
      {isOpen && (
        <div className={`right-side-menu ${isOpen ? 'open' : ''}`}>
        {/* Close Button */}
        <button className="menu-close-btn" onClick={onClose} aria-label="Close menu">
          <X size={24} />
        </button>

        {/* User Profile Section */}
        <div className="menu-profile-section">
          <div className="menu-user-avatar">
            <User size={28} />
          </div>
          <div className="menu-user-info">
            <h3 className="menu-user-name">{user?.fullName || 'User'}</h3>
            <p className="menu-user-email">{user?.email || ''}</p>
            <span 
              className="menu-plan-badge" 
              style={{ background: planBadge.color }}
            >
              {planBadge.text}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="menu-divider"></div>

        {/* Menu Options */}
        <nav className="menu-nav">
          {studioRole !== 'student' && studioRole !== 'teacher' && (
            <button className="menu-item" onClick={handleDashboard}>
              <div className="menu-item-icon">
                <Home size={22} />
              </div>
              <div className="menu-item-content">
                <span className="menu-item-title">{t.dashboard || 'Dashboard'}</span>
                <span className="menu-item-subtitle">
                  {t.dashboardSubtitle || 'Content Creator'}
                </span>
              </div>
            </button>
          )}

          {/* PIXEL AI - AI Tutor Beta */}
          <button className="menu-item beta-item" onClick={handlePixelAI}>
            <div className="menu-item-icon beta-icon">
              <Sparkles size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                <span className="inline-beta-badge">BETA</span>
                {t.pixelAI?.menuTitle || 'PIXEL AI'}
              </span>
              <span className="menu-item-subtitle">
                {t.pixelAI?.menuSubtitle || 'AI Tutor & Problem Solver'}
              </span>
            </div>
          </button>

          <button className="menu-item" onClick={handleStudio}>
            <div className="menu-item-icon">
              <GraduationCap size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                {t.studio?.menuTitle || 'AnimLearn Studio'}
              </span>
              <span className="menu-item-subtitle">
                {t.studio?.menuSubtitle || 'Courses, Assignments & Practice'}
              </span>
            </div>
          </button>

          <button className="menu-item" onClick={handleMyVideos}>
            <div className="menu-item-icon">
              <Video size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">{t.myVideos || 'My Videos'}</span>
              <span className="menu-item-subtitle">
                {t.pricing?.viewAllCreations || 'View all your creations'}
              </span>
            </div>
          </button>

          <button className="menu-item" onClick={handleGallery}>
            <div className="menu-item-icon">
              <Users size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                {t.publicGallery?.menuTitle || 'Community Gallery'}
              </span>
              <span className="menu-item-subtitle">
                {t.publicGallery?.menuSubtitle || 'Explore public creations'}
              </span>
            </div>
          </button>

          <button className="menu-item" onClick={handleCreditHistory}>
            <div className="menu-item-icon">
              <History size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                {t.creditHistory?.menuTitle || 'Credit History'}
              </span>
              <span className="menu-item-subtitle">
                {t.creditHistory?.menuSubtitle || 'View transaction history'}
              </span>
            </div>
          </button>

          {/* Hidden - Change Plan feature not yet implemented
          <button className="menu-item" onClick={handleChangePlan}>
            <div className="menu-item-icon">
              <Gem size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                {t.pricing?.changePlan || 'Change Plan'}
              </span>
              <span className="menu-item-subtitle">
                {t.pricing?.upgradeDowngrade || 'Upgrade or downgrade'}
              </span>
            </div>
          </button>
          */}
          
          <button className="menu-item" onClick={handleSettings}>
            <div className="menu-item-icon">
              <Settings size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">
                {t.settings?.menuTitle || 'Settings'}
              </span>
              <span className="menu-item-subtitle">
                {t.settings?.menuSubtitle || 'Profile & security'}
              </span>
            </div>
          </button>

          <button className="menu-item" onClick={handleContactSupport}>
            <div className="menu-item-icon">
              <Mail size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">{'Support'}</span>
              <span className="menu-item-subtitle">
                {'hello@animlearn.com'}
              </span>
            </div>
          </button>

          <button className="menu-item logout-item" onClick={handleLogout}>
            <div className="menu-item-icon">
              <LogOut size={22} />
            </div>
            <div className="menu-item-content">
              <span className="menu-item-title">{t.logout || 'Logout'}</span>
              <span className="menu-item-subtitle">
                {t.pricing?.signOut || 'Sign out of your account'}
              </span>
            </div>
          </button>
        </nav>

        {/* Footer */}
        <div className="menu-footer">
          <p className="menu-footer-text">
            AnimLearn v1.0
          </p>
        </div>
      </div>
      )}

      {/* Credit History Modal - Always rendered */}
      <CreditHistory 
        isOpen={showCreditHistory}
        onClose={handleCloseCreditHistory}
        userId={user?.id}
      />

      {/* Support Modal - Always rendered */}
      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Feedback Modal - Always rendered */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onLogoutConfirm={handleLogoutConfirm}
      />
    </>
  );
};

export default RightSideMenu;